import React, { useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { useQueryClient } from "@tanstack/react-query";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import useJwt from "./hooks/useJwt";
import RepSearch, { VolumeRep } from "./rep-search";
import { updateCartVolumeRep } from "@/networking/carts.api";
import { UserCompany } from "@/networking/user-companies.api";

interface VolumeCardProps {
  cvTotal: number | undefined;
  qvTotal: number | undefined;
  cartToken?: string;
  buyerRep?: VolumeRep;
  volumeRep?: VolumeRep;
  onOOBDataChange?: (data: {
    order_on_behalf_of: boolean;
    obo_credit_rep_id?: number;
  }) => void;
}

export default function VolumeCard({
  cvTotal,
  qvTotal,
  cartToken,
  volumeRep,
  buyerRep,
  onOOBDataChange,
}: VolumeCardProps) {
  const { jwt, decodedJwt } = useJwt();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showSearchInput, setShowSearchInput] = useState<boolean>(false);
  const [selectedRep, setSelectedRep] = useState<VolumeRep | null>(null);
  const [isUpdatingVolumeRep, setIsUpdatingVolumeRep] =
    useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAccordionOpen, setIsAccordionOpen] = useState<boolean>(false);

  if (decodedJwt && !selectedRep && buyerRep) {
    if (volumeRep) {
      setSelectedRep({
        ...volumeRep,
      });
    } else {
      setSelectedRep({
        ...buyerRep,
      });
    }
  }

  const handleSelectRep = async (rep: UserCompany) => {
    setSelectedRep({
      id: rep.id,
      full_name: rep.first_name + " " + rep.last_name || "",
      email: rep.user?.email || "",
      image_url: rep?.image_url || "",
    });
    setShowSearchInput(false);
    setError(null);

    if (cartToken && rep) {
      await updateVolumeRepInCart(rep.id);
    }

    onOOBDataChange?.({
      order_on_behalf_of: true,
      obo_credit_rep_id: rep.id,
    });
  };

  const updateVolumeRepInCart = async (repId: number) => {
    if (!cartToken) return;

    setIsUpdatingVolumeRep(true);
    setError(null);

    try {
      await updateCartVolumeRep(cartToken, repId, jwt as string);
      queryClient.invalidateQueries({ queryKey: ["cart", cartToken] });
    } catch (err) {
      console.error("Error updating volume rep:", err);
      setError(
        err instanceof Error ? err.message : t("failed_to_update_volume_rep"),
      );
    } finally {
      setIsUpdatingVolumeRep(false);
    }
  };

  const repInitials =
    selectedRep?.full_name
      ?.split(" ")
      .map((name) => name.charAt(0).toUpperCase())
      .join("") || "";

  if (!selectedRep) return null;

  return (
    <div
      className={`relative z-5 mt-4 mb-6 rounded-sm border-b border-gray-200 bg-white pb-2`}
    >
      {error && (
        <div className="mb-3 rounded-sm border border-red-200 bg-red-50 p-2">
          <div className="text-sm text-red-600">{error}</div>
        </div>
      )}
      <Accordion
        type="single"
        collapsible
        className="w-full"
        onValueChange={(value) => setIsAccordionOpen(value === "points")}
      >
        <AccordionItem value="points" className="border-b-0">
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-col items-start justify-between">
              <div className="mb-1 text-sm text-gray-400">
                {t("order_credit")}
              </div>
              <div className="mt-2 mb-1 text-sm font-medium text-gray-900">
                {decodedJwt?.id === selectedRep.id
                  ? t("you_will_earn")
                  : `${selectedRep.full_name.split(" ")[0]} ${t(
                      "will_earn",
                    )}`}{" "}
                QV {qvTotal || 0} CV {cvTotal || 0} 🎉
              </div>
            </div>
            <AccordionTrigger className="m-0 ml-2 flex h-auto min-h-0 w-auto cursor-pointer rounded-sm py-0 hover:bg-gray-100 hover:no-underline" />
          </div>

          <AccordionContent allowOverflow={true} className="py-1">
            <div className="bg-gray-50 p-4">
              {showSearchInput ? (
                <div className="mb-3 flex items-center justify-between">
                  <div className="mr-4 flex-1">
                    <RepSearch
                      selectedRep={selectedRep}
                      onSelect={handleSelectRep}
                      disabled={isUpdatingVolumeRep}
                      title={t("rep_name")}
                      customerId={decodedJwt?.id}
                      isAccordionOpen={isAccordionOpen}
                    />
                  </div>
                  <button
                    onClick={() => setShowSearchInput(false)}
                    className="shrink-0 cursor-pointer text-sm whitespace-nowrap text-blue-600 hover:text-blue-800"
                    disabled={isUpdatingVolumeRep}
                  >
                    {t("cancel")}
                  </button>
                </div>
              ) : (
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-300 text-xs font-medium text-gray-700">
                      {selectedRep.image_url ? (
                        <img
                          src={selectedRep.image_url}
                          alt={selectedRep.full_name}
                          className="h-10 w-10 rounded-full"
                        />
                      ) : (
                        repInitials
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-blue-600">
                        {selectedRep.full_name}
                      </div>
                      <div className="text-xs text-gray-900">
                        <span className="font-medium">{t("rep_id")}:</span>{" "}
                        {selectedRep.id}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowSearchInput(true)}
                    className="cursor-pointer text-sm text-blue-600 hover:text-blue-800"
                    disabled={isUpdatingVolumeRep}
                  >
                    {isUpdatingVolumeRep ? t("updating") : t("change")}
                  </button>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
